import { useState, useRef, useEffect, useCallback } from 'react'
import { CustomAssetManager } from '../lib/CustomAssetManager'

// ── Design tokens ─────────────────────────────────────────────────────────────

const C = {
  bg:                 '#11131b',
  bgLow:              '#191b23',
  bgMid:              '#1d1f28',
  bgHigh:             '#282a32',
  bgHighest:          '#33343d',
  onSurface:          '#e1e1ed',
  onSurfaceVar:       '#c5c5d9',
  outline:            '#8f8fa2',
  outlineVar:         '#454556',
  primary:            '#bec2ff',
  primaryContainer:   '#4d5bff',
  onPrimaryContainer: '#f7f4ff',
  tertiary:           '#00dce5',
  error:              '#ffb4ab',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtTime(seconds) {
  if (!isFinite(seconds) || seconds < 0) return '00:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function fmtDuration(ms) {
  return fmtTime((ms ?? 0) / 1000)
}

// ── Primitives ────────────────────────────────────────────────────────────────

function CapsLabel({ children, color }) {
  return (
    <span style={{
      fontFamily: 'var(--m-font)', fontSize: 10, fontWeight: 700,
      letterSpacing: '.12em', textTransform: 'uppercase',
      color: color ?? C.primary,
    }}>
      {children}
    </span>
  )
}

function FieldLabel({ children }) {
  return (
    <div style={{
      fontFamily: 'var(--m-font)', fontSize: 9.5, fontWeight: 700,
      letterSpacing: '.1em', textTransform: 'uppercase',
      color: C.onSurfaceVar, marginBottom: 4,
    }}>
      {children}
    </div>
  )
}

function StyledInput({ value, onChange, placeholder }) {
  return (
    <input
      value={value ?? ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%', boxSizing: 'border-box',
        background: C.bg, border: `1px solid ${C.outlineVar}`,
        borderRadius: 4, padding: '6px 10px',
        color: C.onSurface, fontSize: 12, fontFamily: 'var(--m-font)',
        outline: 'none',
      }}
    />
  )
}

function Btn({ children, onClick, primary, danger, disabled, small }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: small ? '4px 10px' : '5px 14px',
        borderRadius: 4,
        fontSize: small ? 10 : 11,
        fontFamily: 'var(--m-font)',
        cursor: disabled ? 'default' : 'pointer',
        background: primary ? C.primaryContainer : danger ? C.error + '18' : 'transparent',
        border: `1px solid ${primary ? C.primaryContainer : danger ? C.error + '55' : C.outlineVar}`,
        color: primary ? C.onPrimaryContainer : danger ? C.error : C.onSurfaceVar,
        opacity: disabled ? 0.4 : 1,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
      }}
    >
      {children}
    </button>
  )
}

function Chip({ children }) {
  return (
    <span style={{
      padding: '2px 7px',
      background: C.bgHighest,
      border: `1px solid ${C.outlineVar}`,
      borderRadius: 3,
      fontSize: 9.5, fontFamily: 'var(--m-font)',
      letterSpacing: '.1em', textTransform: 'uppercase',
      color: C.onSurfaceVar, fontWeight: 700,
    }}>
      {children}
    </span>
  )
}

// ── Drop zone ─────────────────────────────────────────────────────────────────

function AudioDropZone({ onAdded }) {
  const [dragging, setDragging]   = useState(false)
  const [pending, setPending]     = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError]         = useState(null)
  const inputRef = useRef()
  const audioRef = useRef(null)

  const isAudio = f => ['wav', 'mp3', 'ogg'].includes(f.name.split('.').pop().toLowerCase())

  const handleFile = useCallback(file => {
    if (!isAudio(file)) {
      setError(`Unsupported: .${file.name.split('.').pop()} — use WAV, MP3, or OGG`)
      return
    }
    const objectUrl = URL.createObjectURL(file)
    setPending({ file, objectUrl, name: file.name.replace(/\.[^.]+$/, ''), transcript: '' })
    setError(null)
  }, [])

  const onDrop = useCallback(e => {
    e.preventDefault(); setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const onPick = useCallback(e => {
    const file = e.target.files[0]
    if (file) handleFile(file)
    e.target.value = ''
  }, [handleFile])

  const playPreview = useCallback(() => {
    if (!pending?.objectUrl) return
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
    const a = new Audio(pending.objectUrl)
    audioRef.current = a
    a.play().catch(() => {})
  }, [pending])

  const discard = useCallback(() => {
    if (pending?.objectUrl) URL.revokeObjectURL(pending.objectUrl)
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
    setPending(null); setError(null)
  }, [pending])

  const doAdd = useCallback(async () => {
    if (!pending || !pending.name) return
    setUploading(true); setError(null)
    try {
      await CustomAssetManager.uploadAudioAsset(pending.file, {
        name: pending.name,
        transcript: pending.transcript || undefined,
      })
      URL.revokeObjectURL(pending.objectUrl)
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
      setPending(null)
      onAdded?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setUploading(false)
    }
  }, [pending, onAdded])

  return (
    <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.outlineVar}`, flexShrink: 0 }}>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !pending && inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? C.primary : C.outlineVar}`,
          borderRadius: 6,
          padding: pending ? '16px' : '22px 16px',
          textAlign: pending ? 'left' : 'center',
          cursor: pending ? 'default' : 'pointer',
          background: dragging ? C.primary + '08' : C.bgMid,
          transition: 'border-color .15s, background .15s',
        }}
      >
        <input ref={inputRef} type="file" accept=".wav,.mp3,.ogg" onChange={onPick} style={{ display: 'none' }} />

        {pending ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <CapsLabel>Staged</CapsLabel>
              <button
                onClick={playPreview}
                style={{
                  background: C.primary + '20', border: `1px solid ${C.primary}44`,
                  borderRadius: 4, color: C.primary, fontSize: 10, fontFamily: 'var(--m-font)',
                  cursor: 'pointer', padding: '3px 10px', letterSpacing: '.06em',
                  textTransform: 'uppercase', fontWeight: 600,
                }}
              >
                ▶ Preview
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <FieldLabel>Name</FieldLabel>
                <StyledInput value={pending.name} onChange={v => setPending(p => ({ ...p, name: v }))} placeholder="clip name" />
              </div>
              <div>
                <FieldLabel>Transcript (optional)</FieldLabel>
                <StyledInput value={pending.transcript} onChange={v => setPending(p => ({ ...p, transcript: v }))} placeholder="spoken text…" />
              </div>
            </div>
            {error && <div style={{ fontSize: 10, color: C.error, marginBottom: 8, fontFamily: 'var(--m-font)' }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn primary onClick={doAdd} disabled={uploading || !pending.name}>
                {uploading ? 'Adding…' : 'Add Clip'}
              </Btn>
              <Btn onClick={discard}>Discard</Btn>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 26, marginBottom: 6, color: dragging ? C.primary : C.outline, opacity: dragging ? 1 : 0.6 }}>⬆</div>
            <div style={{ fontSize: 13, color: C.onSurface, fontFamily: 'var(--m-font)', fontWeight: 600, marginBottom: 4 }}>
              Drop audio or click to import
            </div>
            <div style={{ fontSize: 10, color: C.onSurfaceVar, fontFamily: 'var(--m-font)', letterSpacing: '.14em', textTransform: 'uppercase' }}>
              WAV • MP3 • OGG
            </div>
            {error && <div style={{ fontSize: 10, color: C.error, marginTop: 6, fontFamily: 'var(--m-font)' }}>{error}</div>}
          </>
        )}
      </div>
    </div>
  )
}

// ── Progress timeline ─────────────────────────────────────────────────────────

function ProgressBar({ currentTime, duration, onSeek }) {
  const pct = duration > 0 ? Math.min(currentTime / duration, 1) : 0
  const barRef = useRef()

  const handleClick = e => {
    if (!barRef.current || !onSeek || duration <= 0) return
    const rect = barRef.current.getBoundingClientRect()
    onSeek(((e.clientX - rect.left) / rect.width) * duration)
  }

  return (
    <div
      ref={barRef}
      onClick={handleClick}
      style={{
        width: '100%', height: 4, background: C.outlineVar, borderRadius: 2,
        cursor: onSeek && duration > 0 ? 'pointer' : 'default',
        position: 'relative',
      }}
    >
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0,
        width: `${pct * 100}%`, background: C.primary, borderRadius: 2,
      }} />
      <div style={{
        position: 'absolute', top: '50%', left: `${pct * 100}%`,
        width: 10, height: 10, borderRadius: '50%', background: C.primary,
        transform: 'translate(-50%, -50%)',
        boxShadow: `0 0 6px ${C.primary}88`,
      }} />
    </div>
  )
}

// ── Clip detail ───────────────────────────────────────────────────────────────

function ClipDetail({ asset, onDelete }) {
  const [confirming, setConfirming]       = useState(false)
  const [deleteError, setDeleteError]     = useState(null)
  const [isPlaying, setIsPlaying]         = useState(false)
  const [currentTime, setCurrentTime]     = useState(0)
  const [audioDuration, setAudioDuration] = useState(asset.duration_ms ? asset.duration_ms / 1000 : 0)
  const audioRef = useRef(null)

  useEffect(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
    setIsPlaying(false); setCurrentTime(0)
    setAudioDuration(asset.duration_ms ? asset.duration_ms / 1000 : 0)
    setConfirming(false); setDeleteError(null)
  }, [asset.id])

  const togglePlay = () => {
    if (!asset.file_url) return
    if (isPlaying) {
      audioRef.current?.pause()
      setIsPlaying(false)
      return
    }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
    const a = new Audio(asset.file_url)
    audioRef.current = a
    a.ontimeupdate     = () => setCurrentTime(a.currentTime)
    a.onloadedmetadata = () => setAudioDuration(a.duration)
    a.onended          = () => { setIsPlaying(false); setCurrentTime(0) }
    a.play().then(() => setIsPlaying(true)).catch(() => {})
  }

  const seek = t => {
    setCurrentTime(t)
    if (audioRef.current) audioRef.current.currentTime = t
  }

  const doDelete = async () => {
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null }
      await onDelete(asset.id)
    } catch (e) {
      setDeleteError(e.message)
      setConfirming(false)
    }
  }

  return (
    <div style={{
      padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16,
      height: '100%', boxSizing: 'border-box', borderRight: `1px solid ${C.outlineVar}`,
    }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ minWidth: 0 }}>
          <CapsLabel>Audio Clip</CapsLabel>
          <div style={{
            fontSize: 22, fontWeight: 700, color: C.onSurface, marginTop: 4,
            fontFamily: 'var(--m-font)', letterSpacing: '-.01em',
            lineHeight: 1.2, wordBreak: 'break-all',
          }}>
            {asset.name}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <div style={{
            background: C.bg, border: `1px solid ${C.outlineVar}`, borderRadius: 4,
            padding: '5px 10px', fontFamily: 'var(--m-mono)', fontSize: 12,
            color: C.tertiary, letterSpacing: '.05em', whiteSpace: 'nowrap',
          }}>
            {fmtTime(currentTime)} / {fmtTime(audioDuration)}
          </div>
          <button
            onClick={togglePlay}
            style={{
              width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
              background: C.primaryContainer, border: 'none',
              color: C.onPrimaryContainer, fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
        </div>
      </div>

      {/* Timeline */}
      <ProgressBar currentTime={currentTime} duration={audioDuration} onSeek={seek} />

      {/* Chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {asset.duration_ms && <Chip>{fmtDuration(asset.duration_ms)}</Chip>}
        {asset.transcript && <Chip>Transcript</Chip>}
        <Chip>Audio</Chip>
      </div>

      {/* Transcript */}
      {asset.transcript && (
        <div>
          <FieldLabel>Transcript</FieldLabel>
          <div style={{
            fontSize: 12, color: C.onSurfaceVar, fontFamily: 'var(--m-font)',
            lineHeight: 1.6, background: C.bg, border: `1px solid ${C.outlineVar}`,
            borderRadius: 4, padding: '8px 10px', wordBreak: 'break-word',
          }}>
            {asset.transcript}
          </div>
        </div>
      )}

      {/* Delete */}
      <div style={{ marginTop: 'auto' }}>
        {deleteError && (
          <div style={{ fontSize: 10, color: C.error, marginBottom: 8, fontFamily: 'var(--m-font)' }}>{deleteError}</div>
        )}
        {confirming ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: C.onSurfaceVar, fontFamily: 'var(--m-font)' }}>Delete this clip?</span>
            <Btn danger onClick={doDelete}>Delete</Btn>
            <Btn onClick={() => setConfirming(false)}>Cancel</Btn>
          </div>
        ) : (
          <Btn danger small onClick={() => { setDeleteError(null); setConfirming(true) }}>Delete clip</Btn>
        )}
      </div>
    </div>
  )
}

// ── Clip list ─────────────────────────────────────────────────────────────────

function ClipList({ assets, selected, onSelect }) {
  const playQuick = (e, url) => {
    e.stopPropagation()
    if (!url) return
    new Audio(url).play().catch(() => {})
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{
        padding: '10px 14px', borderBottom: `1px solid ${C.outlineVar}`, flexShrink: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <CapsLabel color={C.onSurface}>Imported Clips</CapsLabel>
        <span style={{ fontFamily: 'var(--m-mono)', fontSize: 10, color: C.onSurfaceVar }}>{assets.length}</span>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {assets.length === 0 && (
          <div style={{ padding: '14px 16px', fontSize: 10, color: C.outline, fontFamily: 'var(--m-font)' }}>
            No clips yet
          </div>
        )}
        {assets.map(a => {
          const active = selected?.id === a.id
          return (
            <div
              key={a.id}
              onClick={() => onSelect(a)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                background: active ? C.bgHighest : 'transparent',
                borderLeft: `2px solid ${active ? C.primary : 'transparent'}`,
                borderBottom: `1px solid ${C.outlineVar}`,
                cursor: 'pointer',
              }}
            >
              <button
                onClick={e => playQuick(e, a.file_url)}
                title="Play"
                style={{
                  flexShrink: 0, background: 'none', border: 'none',
                  color: active ? C.primary : C.outline,
                  cursor: 'pointer', padding: 2, fontSize: 13, lineHeight: 1,
                }}
              >
                ▶
              </button>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{
                  fontSize: 12, fontFamily: 'var(--m-font)',
                  color: active ? C.onSurface : C.onSurfaceVar,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontWeight: active ? 600 : 400,
                }}>
                  {a.name}
                </div>
                {a.duration_ms && (
                  <div style={{ fontSize: 10, color: C.outline, fontFamily: 'var(--m-mono)', marginTop: 1 }}>
                    {fmtDuration(a.duration_ms)}
                  </div>
                )}
              </div>
              <span style={{
                fontFamily: 'var(--m-font)', fontSize: 9, fontWeight: 700,
                letterSpacing: '.12em', textTransform: 'uppercase',
                color: active ? C.primary : C.outline, flexShrink: 0,
              }}>
                Ready
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function AudioAssetsPanel() {
  const [audioAssets, setAudioAssets] = useState([])
  const [selected, setSelected]       = useState(null)

  const refresh = useCallback(() => {
    setAudioAssets(CustomAssetManager.listAudioAssets())
  }, [])

  useEffect(() => {
    refresh()
    return CustomAssetManager.subscribe(refresh)
  }, [refresh])

  const handleDelete = async id => {
    await CustomAssetManager.deleteAudioAsset(id)
    if (selected?.id === id) setSelected(null)
  }

  return (
    <div style={{
      width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
      background: C.bg, color: C.onSurface,
      fontFamily: 'var(--m-font)', overflow: 'hidden',
    }}>
      <AudioDropZone onAdded={refresh} />

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '2fr 1fr', overflow: 'hidden' }}>
        <div style={{ overflowY: 'auto' }}>
          {selected
            ? <ClipDetail asset={selected} onDelete={handleDelete} />
            : (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100%', flexDirection: 'column', gap: 8, color: C.outline,
              }}>
                <div style={{ fontSize: 28, opacity: 0.3 }}>♪</div>
                <div style={{ fontSize: 11, fontFamily: 'var(--m-font)', letterSpacing: '.08em' }}>Select a clip to inspect</div>
              </div>
            )
          }
        </div>

        <ClipList assets={audioAssets} selected={selected} onSelect={setSelected} />
      </div>
    </div>
  )
}
